// Who is on duty on a date — the operational read that Start Session (0085)
// made both possible and necessary.
//
// ⚠ WHY IT IS HERE. A therapist is dispatchable only through a started Therapist
// Session past 2:00 PM — clock-in, the other door, was retired in 0098. Before
// this card, "why is nobody available in Cainta?" had no answer anywhere in the
// admin app — the approval queue below shows next week's paperwork, and
// `pick_therapists` is a SQL function with no surface. A hub manager can now see
// that four therapists hold today and none of them tapped Start.
//
// ⚠ ANY DATE, NOT ONLY TODAY. The override routes accept any date that has not
// started, so a sick call for tomorrow can be handled tonight; the card used to
// be pinned to today and could not reach it.
//
// The states are the Therapist Session State Machine's, DERIVED from the clock
// rather than stored, so this card and the therapist's own screen cannot
// disagree — both read `therapistSessionPhase`.

import { manilaDateOf, type TherapistSessionPhase, therapistSessionPhase } from "@bednbody/logic";
import { Badge } from "@bednbody/ui/components/badge";
import { Button } from "@bednbody/ui/components/button";
import { SurfaceCard } from "@bednbody/ui/components/surface-card";
import { Empty, EmptyDescription, EmptyHeader, EmptyTitle } from "@bednbody/ui/components/empty";
import { Input } from "@bednbody/ui/components/input";
import { Skeleton } from "@bednbody/ui/components/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@bednbody/ui/components/table";
import { ArrowsLeftRightIcon, StopCircleIcon } from "@phosphor-icons/react";
import { useState } from "react";
import { useAuth } from "@/lib/auth";
import { EndEmergencyPauseDialog } from "./EndEmergencyPauseDialog";
import { TherapistSessionOverrideDialog } from "./TherapistSessionOverrideDialog";
import {
  type TherapistSessionTodayRow,
  useEndTherapistEmergencyPause,
  useOffTherapistDays,
  useReassignTherapistSession,
  useRetimeTherapistSession,
  useSetTherapistSessionState,
  useTherapistSessionsToday,
} from "./therapist-sessions.hooks";

function manilaTime(iso: string | null): string {
  if (!iso) return "—";
  return new Intl.DateTimeFormat("en-PH", {
    timeZone: "Asia/Manila",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  }).format(new Date(iso));
}

/**
 * ⚠ `off_final` is NOT a missing session — it is a first-class state
 * (TSESSION-034). Rendering it as "not started" would read as a therapist who
 * failed to show up, which is a different and much more serious claim.
 */
function statusFor(
  state: string,
  phase: TherapistSessionPhase,
): { label: string; variant: "success" | "info" | "warning" | "muted" } {
  if (state !== "secured_final") return { label: "Off", variant: "muted" };
  switch (phase) {
    case "before_window":
      // Start has not unlocked yet (1:00 PM) — nobody is late. Calling this
      // "Not started" put every future date in the warning colour.
      return { label: "Secured", variant: "info" };
    case "available":
      return { label: "Available", variant: "success" };
    case "waiting":
      return { label: "Waiting", variant: "info" };
    case "ended":
      return { label: "Ended", variant: "muted" };
    default:
      // Secured, Start unlocked, and not started. After 2:00 PM this is the row
      // that explains an empty dispatch queue.
      return { label: "Not started", variant: "warning" };
  }
}

/** Today in Manila — between midnight and 8:00 AM a UTC date names yesterday. */
const todayInManila = () => manilaDateOf(new Date().toISOString());

export function TodaySessionsCard() {
  const { claims } = useAuth();
  // 0245 — retime is Superadmin only, narrower than the state/off-days/
  // reassign routes above (admin + superadmin). Hide the option from a plain
  // Admin rather than offer a form the server is certain to 403.
  const canRetime = claims?.role === "superadmin";
  const [serviceDate, setServiceDate] = useState(todayInManila);
  const hasDate = /^\d{4}-\d{2}-\d{2}$/.test(serviceDate);
  const { data, isPending, isError } = useTherapistSessionsToday(serviceDate, hasDate);
  const rows = hasDate ? (data ?? []) : [];
  const isToday = serviceDate === todayInManila();
  const nowIso = new Date().toISOString();

  const [overriding, setOverriding] = useState<TherapistSessionTodayRow | null>(null);
  const setState = useSetTherapistSessionState();
  const offDays = useOffTherapistDays();
  const reassign = useReassignTherapistSession();
  const retime = useRetimeTherapistSession();
  // 0352 — Superadmin only, like retime: TEND-061's "Support" is no role yet.
  const canEndEmergency = claims?.role === "superadmin";
  const [endingBreak, setEndingBreak] = useState<TherapistSessionTodayRow | null>(null);
  const endEmergency = useEndTherapistEmergencyPause();
  const closeEndBreak = () => {
    setEndingBreak(null);
    endEmergency.reset();
  };

  /**
   * OQ-122 — a date may only be moved before she starts it and before its window
   * closes. 0195 refuses anything else, so the button is hidden rather than
   * shown disabled: a control that can never succeed is noise on a monitoring
   * screen.
   */
  const canOverride = (row: TherapistSessionTodayRow) =>
    !row.startedAt && new Date(row.endsAt) > new Date();

  const pending = setState.isPending || offDays.isPending || reassign.isPending || retime.isPending;
  const overrideError = setState.error ?? offDays.error ?? reassign.error ?? retime.error;

  const closeOverride = () => {
    setOverriding(null);
    setState.reset();
    offDays.reset();
    reassign.reset();
    retime.reset();
  };

  return (
    <section aria-labelledby="sessions-today-heading" className="flex min-w-0 flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 id="sessions-today-heading" className="type-title-subsection">
          {isToday ? "Sessions today" : "Sessions"} — who is on duty
        </h2>
        <div className="flex items-center gap-2">
          <label htmlFor="sessions-date" className="sr-only">
            Service date
          </label>
          <Input
            id="sessions-date"
            type="date"
            value={serviceDate}
            onChange={(e) => setServiceDate(e.target.value)}
            className="w-auto"
          />
          {isToday ? null : (
            <Button variant="ghost" size="sm" onClick={() => setServiceDate(todayInManila())}>
              Today
            </Button>
          )}
        </div>
      </div>
      <SurfaceCard className="min-w-0 overflow-hidden p-0">
        {!hasDate ? (
          <p className="p-4 text-sm text-muted-foreground">Choose a service date.</p>
        ) : isError ? (
          // ⚠ NOT "No sessions". A failed load that reads as an empty day tells an
          // operator nobody is on duty.
          <p className="p-4 text-sm text-destructive">
            Couldn&rsquo;t load Sessions — check your connection and try again.
          </p>
        ) : isPending ? (
          <div className="p-4"><Skeleton className="h-12 w-full" /></div>
        ) : rows.length === 0 ? (
          <Empty>
            <EmptyHeader>
              <EmptyTitle>No sessions for this date</EmptyTitle>
              <EmptyDescription>
                Sessions appear once a week is finalized at Friday 7:00 PM.
              </EmptyDescription>
            </EmptyHeader>
          </Empty>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Therapist</TableHead>
                <TableHead>Dispatch Area</TableHead>
                <TableHead>Started</TableHead>
                <TableHead className="text-right">Status</TableHead>
                <TableHead className="w-px" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row) => {
                const phase = therapistSessionPhase(nowIso, serviceDate, row.startedAt);
                // An Emergency Pause outranks the clock-derived phase: she is
                // started but out of Auto-Assign, which is what an operator
                // needs to see first.
                const onEmergencyBreak = row.dailyState === "emergency_paused";
                const status = onEmergencyBreak
                  ? { label: "Emergency break", variant: "warning" as const }
                  : statusFor(row.state, phase);
                return (
                  <TableRow key={row.id}>
                    <TableCell>{row.therapistName}</TableCell>
                    <TableCell className="text-muted-foreground">{row.hubName}</TableCell>
                    {/* A Late Start is visible as a time after 2:00 PM rather
                        than as its own column: TEND-019's five outcomes are not
                        all computable yet, and a half-populated outcome column
                        would imply the other three exist. */}
                    <TableCell className="text-muted-foreground">
                      {manilaTime(row.startedAt)}
                    </TableCell>
                    <TableCell className="text-right">
                      <Badge variant={status.variant}>{status.label}</Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      {onEmergencyBreak && canEndEmergency ? (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setEndingBreak(row)}
                          aria-label={`End ${row.therapistName}'s emergency break`}
                        >
                          <StopCircleIcon />
                          End break
                        </Button>
                      ) : null}
                      {canOverride(row) ? (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setOverriding(row)}
                          aria-label={`Move ${row.therapistName}'s date`}
                        >
                          <ArrowsLeftRightIcon />
                          Move
                        </Button>
                      ) : null}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </SurfaceCard>

      <EndEmergencyPauseDialog
        session={endingBreak}
        isPending={endEmergency.isPending}
        errorMessage={endEmergency.error ? endEmergency.error.message : null}
        onClose={closeEndBreak}
        onSubmit={(reason) =>
          endEmergency.mutate({ sessionId: endingBreak!.id, reason }, { onSuccess: closeEndBreak })
        }
      />

      <TherapistSessionOverrideDialog
        session={overriding}
        canRetime={canRetime}
        serviceDate={serviceDate}
        isPending={pending}
        errorMessage={overrideError ? overrideError.message : null}
        onClose={closeOverride}
        onSetState={(input) =>
          setState.mutate({ sessionId: overriding!.id, ...input }, { onSuccess: closeOverride })
        }
        onOffDays={(input) =>
          offDays.mutate(
            {
              cycleId: overriding!.cycleId,
              therapistId: overriding!.therapistId,
              ...input,
            },
            { onSuccess: closeOverride },
          )
        }
        onReassign={(input) =>
          reassign.mutate({ sessionId: overriding!.id, ...input }, { onSuccess: closeOverride })
        }
        onRetime={(input) =>
          retime.mutate({ sessionId: overriding!.id, ...input }, { onSuccess: closeOverride })
        }
      />
    </section>
  );
}
